// project imports
// action - state management
import * as actionTypes from "../../actions/actionTypes";
import { getUnitSymbolByUnit, itemType, unitType } from "../types";
import { years } from "store/utils/years";
import {
  COST_OF_SALES_CONTD_REDUCER_KEY,
  CUMULATIVE_MEMBERS_REDUCER_KEY,
} from "..";
import { v4 as uuidv4 } from "uuid";
import {
  CUMULATIVE_MEMBERS_KEY,
  TOTAL_KEY,
} from "../subscriber-acquisitions/cumulativeMembersReducer";
import {
  concurrentAmountMonths,
  concurrentRatioKeyMonths,
  fixedCostKeyMonth,
  numberOfAvgCumMembersMonth,
  numberOfConcurrentMonths,
  serverCostMonths,
} from "./staticYearsData";

// This is to filter based on company types
import { SaaSCoType, GeneralCoType } from "constants/companyTypes";

export const SERVER_COST_KEY = "serverCostKey";
export const CONCURRENT_AMOUNT_KEY = "concurrentAmountKey";
export const CONCURRENT_RATIO_KEY = "concurrentRatioKey";
export const NUMBER_OF_CONCURRENT_KEY = "numberOfConcurrentKey";
export const NUMBER_OF_AVG_CUM_MEMBER_KEY = "numberOfAvgCumMembers";
export const FIXED_COST_KEY = "fixedCostKey";

export const costOfSalesContdItems = [
  SERVER_COST_KEY,
  CONCURRENT_AMOUNT_KEY,
  CONCURRENT_RATIO_KEY,
  NUMBER_OF_CONCURRENT_KEY,
  NUMBER_OF_AVG_CUM_MEMBER_KEY,
  FIXED_COST_KEY,
];

export const initialState = {
  [SERVER_COST_KEY]: {
    title: "Server Cost",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const fixedCost = getReducerFunc(COST_OF_SALES_CONTD_REDUCER_KEY)[
        FIXED_COST_KEY
      ]["years"][yearKey];
      const cumulativeMembers = getReducerFunc(COST_OF_SALES_CONTD_REDUCER_KEY)[
        NUMBER_OF_AVG_CUM_MEMBER_KEY
      ].calculate(yearKey, getReducerFunc, companyType);
      const numberOfConcurrent = getReducerFunc(
        COST_OF_SALES_CONTD_REDUCER_KEY
      )[NUMBER_OF_CONCURRENT_KEY]["years"][yearKey];
      const concurrentRatio = getReducerFunc(COST_OF_SALES_CONTD_REDUCER_KEY)[
        CONCURRENT_RATIO_KEY
      ]["years"][yearKey];
      const concurrentAmount = getReducerFunc(COST_OF_SALES_CONTD_REDUCER_KEY)[
        CONCURRENT_AMOUNT_KEY
      ]["years"][yearKey];
      const result =
        Number(fixedCost) +
        ((cumulativeMembers * concurrentRatio) / 100 < numberOfConcurrent
          ? 0
          : ((cumulativeMembers * concurrentRatio) / 100 / numberOfConcurrent) *
            concurrentAmount);
      return result.toFixed(0);
    },
    months: serverCostMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [CONCURRENT_AMOUNT_KEY]: {
    title: "Concurrent Amount",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 350,
    initialPeriodMonth: 350,
    showInitialPeriod: true,
    isInitialPeriodInput: true,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      let result = getReducerFunc(COST_OF_SALES_CONTD_REDUCER_KEY)[
        CONCURRENT_AMOUNT_KEY
      ]["years"][yearKey - 1];
      if (yearKey === 1) {
        result = getReducerFunc(COST_OF_SALES_CONTD_REDUCER_KEY)[
          CONCURRENT_AMOUNT_KEY
        ]["initialPeriod"];
      }
      return result;
    },
    // years: concurrentAmountMonths,
    months: concurrentAmountMonths,
    years: {
      [years.YEAR_1_KEY]: 350,
      [years.YEAR_2_KEY]: 350,
      [years.YEAR_3_KEY]: 350,
      [years.YEAR_4_KEY]: 350,
      [years.YEAR_5_KEY]: 350,
    },
  },
  [CONCURRENT_RATIO_KEY]: {
    id: uuidv4(),
    title: "Concurrent Ratio",
    unit: unitType.PERCENTAGE,
    unitSymbol: getUnitSymbolByUnit(unitType.PERCENTAGE),
    initialPeriod: 1.5,
    initialPeriodMonth: 1.5,
    showInitialPeriod: true,
    isInitialPeriodInput: true,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      let result = getReducerFunc(COST_OF_SALES_CONTD_REDUCER_KEY)[
        CONCURRENT_RATIO_KEY
      ]["years"][yearKey - 1];
      if (yearKey === 1) {
        result = getReducerFunc(COST_OF_SALES_CONTD_REDUCER_KEY)[
          CONCURRENT_RATIO_KEY
        ]["initialPeriod"];
      }
      return result;
    },
    months: concurrentRatioKeyMonths,
    years: {
      [years.YEAR_1_KEY]: 1.5,
      [years.YEAR_2_KEY]: 1.5,
      [years.YEAR_3_KEY]: 1.5,
      [years.YEAR_4_KEY]: 1.5,
      [years.YEAR_5_KEY]: 1.5,
    },
  },
  [NUMBER_OF_CONCURRENT_KEY]: {
    title: "Number of Concurrent",
    unit: unitType.NUMBER,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 2000,
    initialPeriodMonth: 2000,
    showInitialPeriod: true,
    isInitialPeriodInput: true,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      let result = getReducerFunc(COST_OF_SALES_CONTD_REDUCER_KEY)[
        NUMBER_OF_CONCURRENT_KEY
      ]["years"][yearKey - 1];
      if (yearKey === 1) {
        result = getReducerFunc(COST_OF_SALES_CONTD_REDUCER_KEY)[
          NUMBER_OF_CONCURRENT_KEY
        ]["initialPeriod"];
      }
      return result;
    },
    months: numberOfConcurrentMonths,
    years: {
      [years.YEAR_1_KEY]: 2000,
      [years.YEAR_2_KEY]: 2000,
      [years.YEAR_3_KEY]: 2000,
      [years.YEAR_4_KEY]: 2000,
      [years.YEAR_5_KEY]: 2000,
    },
  },
  [NUMBER_OF_AVG_CUM_MEMBER_KEY]: {
    title: "Number of Avg Cumulative Members",
    unit: unitType.NUMBER,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      // TODO: Update this logic after subscriber acquisitions tab is good to go.
      const cumulativeMembers = getReducerFunc(CUMULATIVE_MEMBERS_REDUCER_KEY)[
        CUMULATIVE_MEMBERS_KEY
      ].calculate(yearKey, getReducerFunc, companyType);

      const total = getReducerFunc(CUMULATIVE_MEMBERS_REDUCER_KEY)[
        TOTAL_KEY
      ].calculate(yearKey, getReducerFunc, companyType);

      const result = (Number(cumulativeMembers) + Number(total)) / 2;

      return result.toFixed(0);
    },
    months: numberOfAvgCumMembersMonth,
    years: {
      [years.YEAR_1_KEY]: 40500,
      [years.YEAR_2_KEY]: 131726,
      [years.YEAR_3_KEY]: 250181,
      [years.YEAR_4_KEY]: 400506,
      [years.YEAR_5_KEY]: 586457,
    },
  },

  [FIXED_COST_KEY]: {
    title: "Fixed Cost",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 1000,
    initialPeriodMonth: 1000,
    showInitialPeriod: true,
    isInitialPeriodInput: true,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      let result = getReducerFunc(COST_OF_SALES_CONTD_REDUCER_KEY)[
        FIXED_COST_KEY
      ]["years"][yearKey - 1];
      if (yearKey === 1) {
        result = getReducerFunc(COST_OF_SALES_CONTD_REDUCER_KEY)[
          FIXED_COST_KEY
        ]["initialPeriod"];
      }
      return result;
    },
    months: fixedCostKeyMonth,
    years: {
      [years.YEAR_1_KEY]: 1000,
      [years.YEAR_2_KEY]: 1000,
      [years.YEAR_3_KEY]: 1000,
      [years.YEAR_4_KEY]: 1000,
      [years.YEAR_5_KEY]: 1000,
    },
  },
};

// ==============================|| SUBSCRIPTION ACQUISITION ASSUMPTIONS REDUCER ||============================== //

const costOfSalesContdReducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.SET_COST_OF_SALE_CONTD_BY_YEAR:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          years: {
            ...state[action.key].years,
            [action.yearKey]: [action.amount],
          },
        },
      };
    case actionTypes.SET_COST_OF_SALE_CONTD_BY_MONTH:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          months: {
            ...state[action.key].months,
            [action.yearKey]: [action.amount],
          },
        },
      };
    case actionTypes.SET_COST_OF_SALE_CONTD_INITIAL_PERIOD:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          initialPeriod: action.initialPeriod,
        },
      };
    case actionTypes.SET_COST_OF_SALE_CONTD_INITIAL_PERIOD_MONTH:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          initialPeriodMonth: action.initialPeriod,
        },
      };
    case actionTypes.REPLACE_COST_OF_SALE_CONTD_YEARS:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          years: action?.payload,
        },
      };
    case actionTypes.REPLACE_COST_OF_SALE_CONTD_MONTHS:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          months: action?.payload,
        },
      };
    case actionTypes.RESET_COST_OF_SALE_CONTD_REDUCER:
      return initialState;
    default:
      return state;
  }
};

export default costOfSalesContdReducer;
